import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import AfterpayRequestModal from "@/components/AfterpayRequestModal";

/**
 * Afterpay price tag — now clickable. Opens the "Pay with Afterpay" request
 * modal prefilled with the treatment + price context passed from the parent.
 *
 * Props:
 *  - price: number or string like "$599"
 *  - treatment (optional): treatment name to prefill in the request modal
 *  - className
 *  - clickable (default true): if false, renders as a read-only badge (legacy behavior)
 */
const AfterpayPriceTag = ({ price, treatment = '', className = "", clickable = true }) => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);

  // Parse numeric price from string or number
  let numPrice = 0;
  if (typeof price === 'number') {
    numPrice = price;
  } else if (typeof price === 'string') {
    const cleaned = price.replace(/[^0-9.]/g, '');
    numPrice = parseFloat(cleaned) || 0;
  }

  if (numPrice <= 0) return null;

  const installment = (numPrice / 4).toFixed(2).replace(/\.00$/, '');
  const isEn = language !== 'fr';

  const content = (
    <>
      <span className="bg-black text-[#b2fce4] text-[9px] font-bold px-1.5 py-0.5 rounded leading-none">
        afterpay
      </span>
      <span className="text-xs text-gray-500 group-hover:text-gray-800 transition-colors">
        {isEn ? `4 x $${installment}` : `4 x ${installment}$`}
      </span>
      {clickable && (
        <span className="text-[9px] text-emerald-600 font-semibold underline-offset-2 hover:underline">
          {isEn ? 'Pay this way →' : 'Payer ainsi →'}
        </span>
      )}
    </>
  );

  if (!clickable) {
    return (
      <div className={`flex items-center gap-1.5 ${className}`} data-testid="afterpay-price-tag">
        {content}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={`group flex items-center gap-1.5 cursor-pointer ${className}`}
        data-testid="afterpay-price-tag"
        aria-label={isEn ? 'Pay this with Afterpay — request invoice' : 'Payer avec Afterpay'}
      >
        {content}
      </button>
      <AfterpayRequestModal
        open={open}
        onClose={() => setOpen(false)}
        treatment={treatment}
        price={numPrice}
      />
    </>
  );
};

export default AfterpayPriceTag;
